import { useState, useCallback } from 'react';
import api from '../api';

const LEADS_PER_PACKAGE = 25;

const buildLeadKey = (row, index) => {
  const raw = [row.Nome || '', row.Telefone || '', row.Emails || '', row.Endereço || '', String(index)].join('|');
  return `${index}-${btoa(unescape(encodeURIComponent(raw))).replace(/=/g, '').slice(0, 12)}`;
};

const buildLeadCode = (key, index) =>
  `LD-${String(index + 1).padStart(4, '0')}-${key.slice(0, 4).toUpperCase()}`;

const formatWhatsappPreview = (value) => {
  const raw = String(value || '').trim();
  if (!raw || raw.toUpperCase() === 'N/A') return '---';
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '---';
  return `${digits.slice(0, 4)}***`;
};

const formatEmailPreview = (value) => {
  const raw = String(value || '').trim();
  if (!raw || raw.toUpperCase() === 'N/A') return '---';
  const firstEmail = raw.split(',')[0].trim();
  if (!firstEmail) return '---';
  const [local, domain] = firstEmail.split('@');
  if (!local) return '---';
  if (!domain) return `${local.slice(0, 3)}***`;
  return `${local.slice(0, 3)}***@${domain.slice(0, 2)}***`;
};

export { LEADS_PER_PACKAGE, buildLeadKey, buildLeadCode, formatWhatsappPreview, formatEmailPreview };

export function useLeadSelection(filteredResults) {
  const [leadSelectionMap, setLeadSelectionMap] = useState({});
  const [leadCartItems, setLeadCartItems] = useState([]);
  const [leadLastClickedIndex, setLeadLastClickedIndex] = useState(null);
  const [leadCheckoutLoading, setLeadCheckoutLoading] = useState(false);
  const [leadLastOrderId, setLeadLastOrderId] = useState('');
  const [leadView, setLeadView] = useState('base');

  const leadRows = filteredResults.slice().reverse();

  const leadRenderableRows = leadRows.map((row, index) => {
    const key = buildLeadKey(row, index);
    return {
      key,
      code: buildLeadCode(key, index),
      estado: row.Estado || 'N/A',
      categoria: row.Categoria || 'N/A',
      fonte: row.Fonte || 'N/A',
      whatsappPreview: formatWhatsappPreview(row.WhatsApp || row.Telefone),
      emailPreview: formatEmailPreview(row.Emails),
      Latitude: row.Latitude,
      Longitude: row.Longitude,
    };
  });

  const leadSelectedItems = Object.values(leadSelectionMap);
  const leadSelectedCount = leadSelectedItems.length;
  const leadSelectedPackages = Math.floor(leadSelectedCount / LEADS_PER_PACKAGE);
  const leadSelectedRemainder = leadSelectedCount % LEADS_PER_PACKAGE;
  const leadSelectedMissingToNextPack =
    leadSelectedCount === 0
      ? LEADS_PER_PACKAGE
      : leadSelectedRemainder === 0
      ? 0
      : LEADS_PER_PACKAGE - leadSelectedRemainder;
  const leadCartCount = leadCartItems.length;
  const leadCartPackages = Math.floor(leadCartCount / LEADS_PER_PACKAGE);

  const clearLeadSelection = useCallback(() => {
    setLeadSelectionMap({});
    setLeadLastClickedIndex(null);
  }, []);

  const handleLeadCheckboxClick = useCallback(
    (event, item, index) => {
      const isShift = Boolean(event?.shiftKey);

      setLeadSelectionMap((prev) => {
        const next = { ...prev };
        const currentlySelected = Boolean(prev[item.key]);
        const shouldSelect = !currentlySelected;

        if (isShift && leadLastClickedIndex !== null) {
          const start = Math.min(leadLastClickedIndex, index);
          const end = Math.max(leadLastClickedIndex, index);
          for (let i = start; i <= end; i++) {
            const rangeItem = leadRenderableRows[i];
            if (!rangeItem) continue;
            if (shouldSelect) next[rangeItem.key] = rangeItem;
            else delete next[rangeItem.key];
          }
        } else {
          if (shouldSelect) next[item.key] = item;
          else delete next[item.key];
        }

        return next;
      });

      setLeadLastClickedIndex(index);
    },
    [leadLastClickedIndex, leadRenderableRows]
  );

  const addSelectionToLeadWallet = useCallback(() => {
    const potentialTotal = leadCartItems.length + leadSelectedCount;
    if (potentialTotal < 25) {
      throw new Error('Você precisa selecionar no mínimo 25 leads para adicionar à carteira.');
    }
    setLeadCartItems((prev) => {
      const merged = new Map(prev.map((item) => [item.key, item]));
      leadSelectedItems.forEach((item) => merged.set(item.key, item));
      return Array.from(merged.values());
    });
    clearLeadSelection();
    setLeadView('cart');
  }, [leadSelectedCount, leadSelectedItems, leadCartItems, clearLeadSelection]);

  const confirmLeadWalletCheckout = useCallback(
    async (pricingTier) => {
      if (leadCartCount < 25) {
        throw new Error('A carteira precisa ter pelo menos 25 leads para confirmar o pagamento.');
      }

      const packs = leadCartCount / LEADS_PER_PACKAGE;
      const categories = Array.from(
        new Set(leadCartItems.map((item) => item.categoria).filter(Boolean))
      );
      const leadCodes = leadCartItems.map((item) => item.code);

      setLeadCheckoutLoading(true);
      try {
        const res = await api.post('/orders', {
          tier: pricingTier || 'standard',
          packs,
          categories,
          notes: `lead_checkout|items=${leadCartCount}|codes=${leadCodes.join(',')}`,
        });
        const createdOrderId = res?.data?.id || '';
        if (createdOrderId) setLeadLastOrderId(createdOrderId);
        setLeadCartItems([]);
        return createdOrderId;
      } finally {
        setLeadCheckoutLoading(false);
      }
    },
    [leadCartCount, leadCartItems]
  );

  return {
    leadView,
    setLeadView,
    leadRenderableRows,
    leadSelectionMap,
    setLeadSelectionMap,
    leadSelectedItems,
    leadSelectedCount,
    leadSelectedPackages,
    leadSelectedMissingToNextPack,
    leadCartItems,
    setLeadCartItems,
    leadCartCount,
    leadCartPackages,
    leadCheckoutLoading,
    leadLastOrderId,
    clearLeadSelection,
    handleLeadCheckboxClick,
    addSelectionToLeadWallet,
    confirmLeadWalletCheckout,
  };
}
